// v2 Tabs, Template «Bild-Tabs» (templates/areas/v2/tabs/templates/image-tabs.html.twig)
// Tabs nach WAI-ARIA-Muster: Klick oder Pfeiltasten/Pos1/Ende wechseln den Tab, Deeplink über #anker.
// Unabhängig von modules/Tabs.js, damit die normalen Tabs unverändert bleiben.
class ImageTabs {
  constructor() {
    this.containers = document.querySelectorAll('.js-image-tabs');
    this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  init() {
    if (this.containers.length === 0) {
      return;
    }

    this.containers.forEach((container) => this.setup(container));

    // Links irgendwo auf der Seite (z. B. Querverweise) wechseln den Tab direkt
    document.addEventListener('click', (event) => {
      const link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
      const tab = link ? this.findTab(link.getAttribute('href').slice(1)) : null;

      if (!tab) return;

      event.preventDefault();
      // Fokus nur bei Tastatur (event.detail === 0), sonst zeigt Chrome nach Maus/Touch den Fokusrahmen
      this.select(tab.closest('.js-image-tabs'), tab, { userAction: false, focus: event.detail === 0, scroll: true, updateHash: true });
    });

    window.addEventListener('hashchange', () => this.fromHash());
    this.fromHash();
  }

  setup(container) {
    const tabs = this.getTabs(container);
    const frame = container.querySelector('.image-tabs-frame');

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.select(container, tab, { userAction: true, updateHash: true });
      });

      tab.addEventListener('keydown', (event) => {
        let next = null;

        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];

        if (next) {
          event.preventDefault();
          this.select(container, next, { userAction: true, focus: true, updateHash: true });
        }
      });
    });

    this.setupReadMore(container);

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      this.placePointer(container);
      this.fitLabels(container);
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.updateReadMore(container), 150);
    });
    this.followPointer(container, 800);
    this.fitLabels(container);
    // nach dem Laden der Schriften messen, sonst stimmen die Zeilenhöhen nicht
    this.updateReadMore(container);
    window.addEventListener('load', () => this.updateReadMore(container));
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.updateReadMore(container));
    }

    // inaktive Bilder einmal kurz «anstupsen», sobald das Modul sichtbar ist
    if (!this.reduceMotion && 'IntersectionObserver' in window && frame) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;

        observer.disconnect();
        setTimeout(() => {
          this.getTabs(container).forEach((tab) => {
            if (tab.classList.contains('is-active') || tab.dataset.seen) return;

            tab.classList.add('is-nudging');
            tab.addEventListener('animationend', () => tab.classList.remove('is-nudging'), { once: true });
          });
        }, 600);
      }, { threshold: 0.4 });

      observer.observe(frame);
    }
  }

  getTabs(container) {
    return Array.prototype.slice.call(container.querySelectorAll('[role="tab"]'));
  }

  findTab(id) {
    if (!id) return null;

    const element = document.getElementById(id);

    return element && element.getAttribute('role') === 'tab' && element.closest('.js-image-tabs') ? element : null;
  }

  select(container, activeTab, options = {}) {
    let activePanel = null;

    this.getTabs(container).forEach((tab) => {
      const isActive = tab === activeTab;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));

      tab.classList.toggle('is-active', isActive);
      tab.classList.remove('is-nudging');
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;

      if (isActive) {
        tab.dataset.seen = '1';
      }

      if (panel) {
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          activePanel = panel;
        }
      }
    });

    if (options.focus) {
      activeTab.focus({ preventScroll: true });
    }

    if (options.updateHash && activeTab.id) {
      history.replaceState(null, '', '#' + activeTab.id);
    }

    this.followPointer(container, this.reduceMotion ? 0 : 650);
    this.fitLabels(container);
    this.updateReadMore(container);

    if (activePanel) {
      // z. B. Slider im Tab neu berechnen, wie bei den normalen Tabs
      document.dispatchEvent(new CustomEvent('tab-changed', { detail: { tab: activePanel } }));
    }

    if (options.scroll) {
      container.querySelector('.image-tabs-frame').scrollIntoView({
        behavior: this.reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    if (options.userAction && activePanel) {
      activePanel.classList.remove('is-entering');
      void activePanel.offsetWidth;
      activePanel.classList.add('is-entering');
      setTimeout(() => activePanel.classList.remove('is-entering'), 600);

      // liegt der neue Inhalt ausserhalb des Bildschirms, sanft nachscrollen,
      // aber höchstens so weit, dass die Bilder oben noch ganz sichtbar bleiben (scroll-margin-top wie bei Ankern)
      const top = activePanel.getBoundingClientRect().top;

      if (top > window.innerHeight * 0.7) {
        const frame = container.querySelector('.image-tabs-frame');
        const frameTop = frame.getBoundingClientRect().top - (parseFloat(getComputedStyle(frame).scrollMarginTop) || 0);
        const distance = Math.min(top - window.innerHeight * 0.38, frameTop);

        if (distance > 0) {
          window.scrollBy({ top: distance, behavior: this.reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }
  }

  // Mobile: Inhalt auf 4 Zeilen des ersten Absatzes kürzen (mit «…»), «Weiterlesen» klappt alles auf
  setupReadMore(container) {
    container.querySelectorAll('.image-tabs-readmore').forEach((button) => {
      const body = document.getElementById(button.getAttribute('aria-controls'));

      if (!body) return;

      button.addEventListener('click', () => this.expand(body, button, true));

      // Tastatur: springt der Fokus in den gekürzten Teil, wird aufgeklappt
      body.addEventListener('focusin', (event) => {
        if (event.target !== body && body.classList.contains('is-collapsed')) {
          this.expand(body, button, false);
        }
      });
    });
  }

  updateReadMore(container) {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    container.querySelectorAll('.image-tabs-readmore').forEach((button) => {
      const body = document.getElementById(button.getAttribute('aria-controls'));
      const panel = button.closest('[role="tabpanel"]');

      if (!body || !panel || body.dataset.expanded) return;

      const firstParagraph = body.querySelector('p:not(.text-overline)');

      body.classList.remove('is-collapsed');
      body.style.maxHeight = '';
      if (firstParagraph) firstParagraph.classList.remove('is-clamped');
      button.hidden = true;

      // nur sichtbare Panels lassen sich messen; die anderen folgen beim Tab-Wechsel
      if (!isMobile || panel.hidden) return;

      // is-collapsed vor dem Messen setzen: overflow:hidden verändert das Margin-Collapsing (z. B. .columns -12px)
      body.classList.add('is-collapsed');
      if (firstParagraph) firstParagraph.classList.add('is-clamped');

      const bodyTop = body.getBoundingClientRect().top;
      const limit = firstParagraph
        ? Math.ceil(firstParagraph.getBoundingClientRect().bottom - bodyTop)
        : 300;

      if (body.scrollHeight <= limit + 4) {
        body.classList.remove('is-collapsed');
        if (firstParagraph) firstParagraph.classList.remove('is-clamped');
        return;
      }

      body.style.maxHeight = limit + 'px';
      button.hidden = false;
      button.setAttribute('aria-expanded', 'false');
    });
  }

  expand(body, button, moveFocus) {
    const firstParagraph = body.querySelector('p.is-clamped');

    if (firstParagraph) firstParagraph.classList.remove('is-clamped');

    body.dataset.expanded = '1';
    body.style.maxHeight = body.scrollHeight + 'px';
    button.setAttribute('aria-expanded', 'true');
    button.hidden = true;

    setTimeout(() => {
      body.classList.remove('is-collapsed');
      body.style.maxHeight = '';
    }, this.reduceMotion ? 0 : 450);

    // der Button verschwindet, deshalb den Fokus in den Inhalt setzen
    if (moveFocus) {
      body.focus({ preventScroll: true });
    }
  }

  // Mobile: senkrechte Titel der inaktiven Bilder so weit verkleinern, dass sie in die Höhe passen (min. 14px)
  fitLabels(container) {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    this.getTabs(container).forEach((tab) => {
      const label = tab.querySelector('.image-tab-label');

      if (!label) return;

      label.style.fontSize = '';

      if (!isMobile || tab.classList.contains('is-active')) return;

      const content = tab.querySelector('.image-tab-content');
      const available = content.clientHeight - (parseFloat(getComputedStyle(content).paddingBottom) || 0);
      let size = parseFloat(getComputedStyle(label).fontSize);

      while (label.scrollHeight > available && size > 14) {
        size -= 1;
        label.style.fontSize = size + 'px';
      }
    });
  }

  placePointer(container) {
    const frame = container.querySelector('.image-tabs-frame');
    const activeTab = container.querySelector('[role="tab"].is-active');

    if (!frame || !activeTab) return;

    const frameRect = frame.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    container.style.setProperty('--image-tabs-pointer-x', (tabRect.left - frameRect.left + tabRect.width / 2) + 'px');
  }

  // während die Kachelbreiten animieren, wandert der Zeiger mit
  followPointer(container, duration) {
    const end = performance.now() + duration;
    const step = () => {
      this.placePointer(container);

      if (performance.now() < end) {
        requestAnimationFrame(step);
      }
    };

    step();
  }

  fromHash() {
    const tab = this.findTab((window.location.hash || '').slice(1));

    if (tab) {
      this.select(tab.closest('.js-image-tabs'), tab, { scroll: true });
    }
  }
}

export default new ImageTabs();
